// COMPLIANCE: STATUS_COLORS violation detected - requires manual review
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Bookmark, User } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Link } from 'react-router-dom';
import { capitalizeWords, decodeHtmlEntities, sanitizePublicationContent, cn } from '@/lib/utils';
import { getCountryFlag } from '@/lib/flagUtils';

import { useLocalizations } from '@/hooks/useLocalizations';
import { MessageButton } from '@/components/MessageButton';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { getPublicationFeaturedImage } from '@/lib/publicationImageUtils';
import { pickTopicImage, useTopicImages } from '@/hooks/useTopicImages';
import { INTERACTION_STATES, TYPOGRAPHY, GRIDS, RESPONSIVE, SPACING, CARDS } from '@/design-system';
import { ORPHAN_SENTINEL_UUID } from '@/constants/config';

interface PublicationCardProps {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featuredImage?: string;
  authorId?: string;
  authorName: string;
  authorVanityUrl?: string;
  authorAvatarUrl?: string;
  authorCountry?: string;
  publishedAt?: string;
  categories?: string[];
  showFavoriteButton?: boolean;
  onRemoveFavorite?: (publicationId: string) => void;
  // English Hub columns for optimized localization (deprecated - use localized* props)
  titleEn?: string;
  excerptEn?: string;
  // Layout variant
  variant?: 'default' | 'horizontal';
  // Hide author info (useful on expert profile pages where author is already known)
  hideAuthorInfo?: boolean;
  /**
   * Whether the author's profile is publicly listed.
   * When false, author info is shown but profile link is disabled.
   */
  authorProfileListed?: boolean;
  /**
   * Pre-translated title from parent page (metadata-first pattern).
   * When provided, skips runtime translation entirely.
   * Parent pages should batch-fetch from localization_metadata table.
   */
  localizedTitle?: string;
  /**
   * Pre-translated excerpt from parent page (metadata-first pattern).
   * When provided, skips runtime translation entirely.
   */
  localizedExcerpt?: string;
  /**
   * Pre-translated slug from parent page (metadata-first pattern).
   * When provided, skips runtime translation entirely.
   */
  localizedSlug?: string;
  /** English slug fallback (slug_en column) - used when localizedSlug not available */
  slugEn?: string;
  /** When true, the card link opens in a new browser tab */
  openInNewTab?: boolean;
  /**
   * Topics attached to this publication. Used only as the last picture
   * fallback: when there is no featured image and no author avatar, the first
   * topic with a feature image supplies one.
   */
  topicIds?: string[];
  /**
   * Card position in its grid. Rotates the topic-picture fallback so
   * neighbouring cards do not all repeat the same topic image.
   */
  topicImageOffset?: number;
}

/**
 * PublicationCard Component - Metadata-First Pattern
 * 
 * This component uses pre-translated content passed from parent pages.
 * Runtime translation is FORBIDDEN in card/listing components per localization architecture.
 * 
 * Parent pages must batch-fetch localized metadata from localization_metadata
 * and pass them via the localizedTitle, localizedExcerpt, and localizedSlug props.
 * 
 * When localized props are not provided, the component falls back to original text.
 * No skeleton loading states are needed (metadata-first = instant render).
 */
export const PublicationCard = memo(function PublicationCard({
  id,
  slug,
  title,
  excerpt,
  featuredImage,
  authorId,
  authorName,
  authorVanityUrl,
  authorAvatarUrl,
  authorCountry,
  publishedAt,
  categories,
  showFavoriteButton = false,
  onRemoveFavorite,
  titleEn,
  excerptEn,
  variant = 'default',
  hideAuthorInfo = false,
  authorProfileListed = true,
  localizedTitle: propsLocalizedTitle,
  localizedExcerpt: propsLocalizedExcerpt,
  localizedSlug: propsLocalizedSlug,
  slugEn,
  openInNewTab,
  topicIds,
  topicImageOffset = 0,
}: PublicationCardProps) {
  const shouldHideAuthor = hideAuthorInfo || authorId === ORPHAN_SENTINEL_UUID || !authorId;
  const { trackPublicationImpression } = useAnalyticsTracking();
  const { lCommon } = useLocalizations();
  const hasTrackedImpression = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Track publication impression only when card is ≥50% visible (IntersectionObserver)
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !id) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedImpression.current) {
          hasTrackedImpression.current = true;
          trackPublicationImpression(id, authorId, 'publication_list');
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [id, authorId, trackPublicationImpression]);

  const handleRemoveFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRemoveFavorite) {
      onRemoveFavorite(id);
    }
  };
  
  
  // STRICT LOCALE MATCHING: Use pre-translated props only when provided.
  // No fallback to original content - if localized content is expected but missing,
  // the card should be hidden by the parent component.
  // For English source content (when localization not needed), original props are used.
  const displayTitle = propsLocalizedTitle ?? title;
  const displayExcerpt = propsLocalizedExcerpt ?? excerpt;
  const displaySlug = propsLocalizedSlug ?? slugEn ?? slug;
  
  // Construct SEO-friendly URL: domain.com/{expert_vanity_url}/{localized_slug}
  // If no vanity URL, use sentinel pattern /a/{slug} for orphan publications
  const publicationUrl = authorVanityUrl ? `/${authorVanityUrl}/${displaySlug}` : `/a/${displaySlug}`;
  
  const isHorizontal = variant === 'horizontal';
  
  // Dynamic featured image: publication image, then author avatar, then topic image.
  // The Sentinel (automated Editorial Team) avatar is never used as a picture.
  const allowedAvatarUrl = authorId && authorId !== ORPHAN_SENTINEL_UUID ? authorAvatarUrl : null;
  const ownImage = getPublicationFeaturedImage(featuredImage) || allowedAvatarUrl || null;
  const needsTopicImage = !ownImage && !!topicIds?.length;
  const { data: topicImages } = useTopicImages(needsTopicImage);
  const rawDisplayImage = ownImage || (needsTopicImage ? pickTopicImage(topicImages, topicIds, topicImageOffset) : null);

  const [imageFailed, setImageFailed] = useState(false);

  // Reset failure state when the source URL changes
  useEffect(() => { setImageFailed(false); }, [rawDisplayImage]);

  const handleImageError = useCallback(() => { setImageFailed(true); }, []);

  // If the image failed, try author avatar as fallback; if that also fails, hide entirely
  const displayImage = imageFailed
    ? (rawDisplayImage !== allowedAvatarUrl && allowedAvatarUrl ? allowedAvatarUrl : null)
    : rawDisplayImage;

  // When the big card image already is the author's avatar (any variant),
  // hide the small duplicate avatar in the author row
  const bigImageIsAvatar = !!allowedAvatarUrl && displayImage === allowedAvatarUrl;
  
  return (
    <Link to={publicationUrl} className="block" target={openInNewTab ? "_blank" : undefined} rel={openInNewTab ? "noopener noreferrer" : undefined}>
      <Card ref={cardRef} className="overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-300 ease-out h-full relative group">
        <div className={cn(isHorizontal || displayImage ? "flex" : "", "h-full")}>
          {/* Featured Image with fallback to author avatar */}
          {displayImage && (
            <div className={cn(
              "overflow-hidden flex-shrink-0 self-stretch",
              isHorizontal
                ? "w-24 sm:w-32 md:w-44 min-h-[7rem] aspect-[4/3]"
                : "w-28 sm:w-32 md:w-40 min-h-[9rem] aspect-[3/4]"
            )}>
              <img
                src={displayImage}
                alt={decodeHtmlEntities(displayTitle)}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                onError={handleImageError}
              />
            </div>
          )}
          
          {/* Content */}
          <div className={cn(isHorizontal ? CARDS.horizontalCompact : CARDS.standard, 'flex-1 flex flex-col', SPACING.listItemGap)}>
            {/* Title - pinned to top */}
            <div className={cn("flex items-start justify-between", SPACING.listItemGap)}>
              <h3 className={cn(
                isHorizontal
                  ? "text-lg sm:text-xl font-semibold"
                  : "text-base sm:text-lg font-semibold",
                'line-clamp-2 group-hover:text-primary transition-colors duration-200 flex-1'
              )}>
              {decodeHtmlEntities(displayTitle).replace(/<[^>]*>/g, '').trim()}
              </h3>

              {showFavoriteButton && onRemoveFavorite && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={handleRemoveFavorite}
                        className={`${INTERACTION_STATES.destructiveGhost.default} flex-shrink-0`}
                      >
                        <Bookmark className="h-4 w-4 fill-yellow-400 text-yellow-500" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {lCommon('labels.remove_from_favorites', { defaultValue: 'Remove from favorites' })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            
            {/* Excerpt - horizontal: grows to fill space; default: fixed */}
            {isHorizontal && displayExcerpt && (
              <div className="flex-1">
                <p className={cn(TYPOGRAPHY.cardDescription, 'line-clamp-4')}>
                  {decodeHtmlEntities(sanitizePublicationContent(displayExcerpt)).replace(/^Autor:\s*.*/gim, '').trim()}
                </p>
              </div>
            )}
            
            {!isHorizontal && displayExcerpt && (
              <div>
                <p className={cn(TYPOGRAPHY.cardDescription, 'line-clamp-3 flex-grow')}>
                  {decodeHtmlEntities(sanitizePublicationContent(displayExcerpt)).replace(/^Autor:\s*.*/gim, '').trim()}
                </p>
              </div>
            )}

            {/* Bottom metadata - pinned to bottom via mt-auto for horizontal */}
            <div className={cn(isHorizontal && 'mt-auto', SPACING.stackSm)}>

              {categories && categories.length > 0 && (
                <div className={cn("flex flex-wrap", SPACING.listItemGapCompact)}>
                  {categories.slice(0, 2).map((category) => (
                    <Badge key={category} variant="secondary">
                      {category}
                    </Badge>
                  ))}
                </div>
              )}

              {!shouldHideAuthor && (
                <div className={cn(RESPONSIVE.flexRowBetween, TYPOGRAPHY.cardDescription)}>
                  <div className={cn(RESPONSIVE.flexRowCenterGap2, 'flex-1')}>
                    {!bigImageIsAvatar && (
                      <Avatar className={isHorizontal ? "h-5 w-5" : "h-6 w-6"}>
                        <AvatarImage src={authorAvatarUrl} alt={authorName} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          <User className={isHorizontal ? "h-3 w-3" : "h-3.5 w-3.5"} />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <span className="flex-1 font-semibold text-foreground">{capitalizeWords(authorName)}</span>
                    {authorCountry && (
                      <span className={isHorizontal ? "text-sm" : "text-base"} title={authorCountry}>
                        {getCountryFlag(authorCountry)}
                      </span>
                    )}
                    {authorId && authorProfileListed !== false && (
                      <div className="flex items-center gap-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                        <MessageButton 
                          userId={authorId}
                          userName={authorName}
                          userAvatar={authorAvatarUrl}
                          size="sm"
                          className={isHorizontal ? "h-5 w-5" : "h-6 w-6"}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </Card>
    </Link>
  );
});
